import React, { useMemo, useState } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { markdownToHtml } from '../utils/markdownRenderers';

const SummaryViewer = ({ markdown, onTextSelected }) => {
  const [copied, setCopied] = useState(false);
  const copyFeedbackDuration = 1500;
  const handleMouseUp = () => {
    if (!onTextSelected) return;
    const selection = window.getSelection();
    const text = selection ? selection.toString().trim() : '';
    if (text) {
      onTextSelected(text);
    }
  };

  const renderedHtml = useMemo(() => (markdown ? markdownToHtml(markdown) : ''), [markdown]);
  const isCopyDisabled = !markdown;
  const statusLabel = copied ? 'Copied' : 'Copy';
  const copyButtonClasses = [
    'rounded-full border border-gray-200 bg-white p-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500',
    isCopyDisabled ? 'cursor-not-allowed opacity-40' : 'hover:border-gray-400',
  ].join(' ');

  const buildPlainText = () => {
    if (typeof document === 'undefined' || !renderedHtml) {
      return markdown.trim();
    }
    const container = document.createElement('div');
    container.innerHTML = renderedHtml;
    const text = container.textContent || container.innerText || markdown;
    return text.trim() || markdown;
  };

  const fallbackCopy = async (textValue) => {
    if (navigator?.clipboard?.writeText) {
      return navigator.clipboard.writeText(textValue);
    }
    const textarea = document.createElement('textarea');
    textarea.value = textValue;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const copyWithRichText = async (plainText, htmlText) => {
    if (
      navigator?.clipboard?.write &&
      typeof ClipboardItem !== 'undefined' &&
      typeof Blob !== 'undefined'
    ) {
      const blobPayload = {
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      };
      const clipboardItem = new ClipboardItem(blobPayload);
      await navigator.clipboard.write([clipboardItem]);
      return;
    }
    await fallbackCopy(plainText);
  };

  const copyText = async () => {
    if (!markdown) return;
    const plainText = buildPlainText();
    const htmlPayload = `<div>${renderedHtml}</div>`;

    try {
      await copyWithRichText(plainText, htmlPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), copyFeedbackDuration);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to copy summary', error);
    }
  };

  return (
    <div className="relative">
      <div className="absolute right-3 top-3 z-10 flex items-center gap-2 rounded-full bg-white/90 px-2 py-1 shadow">
        <button
          type="button"
          className={copyButtonClasses}
          onClick={copyText}
          disabled={isCopyDisabled}
          aria-label="Copy summary to clipboard"
        >
          <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
        </button>
        <span className="text-xs font-medium text-gray-600">
          {statusLabel}
        </span>
      </div>
      <div
        onMouseUp={handleMouseUp}
        className="bg-white border rounded-lg p-4 shadow-sm max-h-[70vh] overflow-y-auto text-gray-900"
      >
        {markdown ? (
          <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
        ) : (
          <p className="text-gray-500">Summary will appear once processing completes.</p>
        )}
      </div>
    </div>
  );
};

export default SummaryViewer;
